// FLEXX Super App v3 — shared localStorage state + constants
// All keys prefixed with flexx_ to avoid conflicts

const K = {
  onboarded: "flexx_onboarded",
  profile: "flexx_user_profile",
  diagnosticDone: "flexx_diagnostic_done",
  diagnosticScore: "flexx_diagnostic_score",
  track: "flexx_track",
  graph: "flexx_knowledge_graph",
  xp: "flexx_xp",
  achievements: "flexx_achievements",
  streak: "flexx_streak",
  gates: "flexx_mastery_gates",
  fxHistory: "flexx_fx_coach_history",
  flashcards: "flexx_flashcards_progress",
  journal: "flexx_journal",
  capstone: "flexx_capstone",
  // Lesson quiz completion (Tier 1 LessonsHub)
  lessonCompletion: "flexx_lesson_completion",
  moduleBonus: "flexx_module_bonus_awarded",
  tier1Grad: "flexx_tier1_graduate",
  // Tier 2 — SuPER System (LessonsHub)
  tier2Grad: "flexx_tier2_graduate",
  tier2GateConfirmed: "flexx_tier2_gate_confirmed",
  // Tier 3 — AI-Powered (SageMaster) (LessonsHub)
  tier3Grad: "flexx_tier3_graduate",
  tier3GateConfirmed: "flexx_tier3_gate_confirmed",
  // Tier 4 — Algo Trading (MT5 + Expert Advisors) (LessonsHub)
  tier4Grad: "flexx_tier4_graduate",
  tier4GateConfirmed: "flexx_tier4_gate_confirmed",
  // Tier 5 — Quantitative Trading (Python · Stats · ML · Live Deployment)
  tier5Grad: "flexx_tier5_graduate",
  tier5GateConfirmed: "flexx_tier5_gate_confirmed",
  // Backend-signed certificates (tier → {cert_id, signature, issued_at, verify_path, badge})
  tierCerts: "flexx_tier_certs",
};

export const THEME = {
  teal: "#01696f",
  tealSoft: "#01696f22",
  tealEdge: "#01696f60",
  gold: "#C9A227",
  goldSoft: "#C9A22722",
  green: "#6daa45",
  red: "#a12c7b",
  dark: "#0d0d0d",
  surface: "#1a1a1a",
  card: "#242424",
  border: "#2a2e36",
  text: "#f0f0f0",
  mute: "#888888",
};

// Levels for XP
export const LEVELS = [
  { min: 0, max: 500, name: "Rookie Trader" },
  { min: 501, max: 1500, name: "Market Student" },
  { min: 1501, max: 3000, name: "Chart Reader" },
  { min: 3001, max: 5000, name: "Setup Builder" },
  { min: 5001, max: 8000, name: "Strategy Operator" },
  { min: 8001, max: Infinity, name: "FLEXX Certified Trader" },
];

export const levelFor = (xp) => {
  const i = LEVELS.findIndex((l) => xp >= l.min && xp <= l.max);
  return { index: i, ...LEVELS[i] };
};

// Market examples by user preference
export const MARKET_EXAMPLES = {
  Forex: ["EUR/USD", "GBP/JPY", "USD/CAD"],
  Crypto: ["BTC/USDT", "ETH/USDT", "SOL/USDT"],
  Stocks: ["AAPL", "TSLA", "NVDA"],
  Indices: ["SPX500", "NAS100", "DJ30"],
};

// --- localStorage helpers ---
const get = (k, d) => {
  try {
    const v = localStorage.getItem(k);
    return v === null ? d : JSON.parse(v);
  } catch (_err) { return d; }
};
const set = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
    // Emit a lightweight mutation event (used by the sync service)
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("flexx:mutation", { detail: { key: k } }));
    }
  } catch (e) { console.debug("[FLEXX state] localStorage write skipped:", e?.message || e); /* private mode / quota — state lives in memory only */ }
};

export const flexxState = {
  isOnboarded: () => get(K.onboarded, false),
  setOnboarded: (v) => set(K.onboarded, v),

  profile: () => get(K.profile, { name: "", market: "Forex", experience: "Complete Beginner", studyTime: "30 min" }),
  saveProfile: (p) => set(K.profile, p),

  isDiagnosticDone: () => get(K.diagnosticDone, false),
  setDiagnosticDone: (v) => set(K.diagnosticDone, v),

  diagnosticScore: () => get(K.diagnosticScore, 0),
  saveDiagnosticScore: (s) => set(K.diagnosticScore, s),

  track: () => get(K.track, null),
  saveTrack: (t) => set(K.track, t),

  graph: () => get(K.graph, { marketStructure: 0, ta: 0, risk: 0, psychology: 0, strategy: 0 }),
  saveGraph: (g) => set(K.graph, g),

  xp: () => get(K.xp, 0),
  addXp: (amount) => {
    const prev = get(K.xp, 0);
    const next = Math.max(0, prev + amount);
    set(K.xp, next);
    const prevLvl = levelFor(prev).index;
    const nextLvl = levelFor(next).index;
    return { prev, next, leveledUp: nextLvl > prevLvl, newLevel: levelFor(next) };
  },

  achievements: () => get(K.achievements, []),
  addAchievement: (id) => {
    const cur = get(K.achievements, []);
    if (cur.includes(id)) return false;
    set(K.achievements, [...cur, id]);
    return true;
  },

  streak: () => get(K.streak, { days: 0, lastVisit: null }),
  pingStreak: () => {
    const today = new Date().toISOString().slice(0, 10);
    const s = get(K.streak, { days: 0, lastVisit: null });
    if (s.lastVisit === today) return s;
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const days = s.lastVisit === yesterday ? s.days + 1 : 1;
    const next = { days, lastVisit: today };
    set(K.streak, next);
    return next;
  },

  gates: () => get(K.gates, {}),
  saveGate: (id, record) => {
    const cur = get(K.gates, {});
    cur[id] = record;
    set(K.gates, cur);
  },

  fxHistory: () => get(K.fxHistory, []),
  saveFxMessage: (m) => {
    const cur = get(K.fxHistory, []);
    const next = [...cur, m].slice(-30);
    set(K.fxHistory, next);
    return next;
  },
  clearFxHistory: () => set(K.fxHistory, []),

  // --- Flashcards ---
  flashcards: () => get(K.flashcards, {}),
  saveFlashcard: (cardId, record) => {
    const cur = get(K.flashcards, {});
    cur[cardId] = record;
    set(K.flashcards, cur);
    return cur;
  },
  seedFlashcardDeck: (cards) => {
    // Called when a mastery gate passes — seed due = 3 days out for any
    // cards not yet seeded in the progress map.
    const cur = get(K.flashcards, {});
    const now = Date.now();
    let added = 0;
    cards.forEach((c) => {
      if (!cur[c.id]) {
        cur[c.id] = { intervalIdx: 0, dueAt: now + 3 * 86400000, reviews: 0, mastered: false };
        added += 1;
      }
    });
    if (added > 0) set(K.flashcards, cur);
    return added;
  },
  seedFlashcardsImmediate: (cards) => {
    // Bulk seed cards due NOW (used for manual "start review" when user
    // wants to review before spaced schedule — keeps UX unblocked)
    const cur = get(K.flashcards, {});
    const now = Date.now();
    cards.forEach((c) => {
      if (!cur[c.id]) cur[c.id] = { intervalIdx: 0, dueAt: now - 1, reviews: 0, mastered: false };
    });
    set(K.flashcards, cur);
    return cur;
  },

  // --- Journal ---
  journal: () => get(K.journal, []),
  saveJournalEntry: (entry) => {
    const cur = get(K.journal, []);
    const next = [entry, ...cur];
    set(K.journal, next);
    return next;
  },
  deleteJournalEntry: (id) => {
    const cur = get(K.journal, []);
    const next = cur.filter((e) => e.id !== id);
    set(K.journal, next);
    return next;
  },
  updateJournalEntry: (id, patch) => {
    const cur = get(K.journal, []);
    const next = cur.map((e) => (e.id === id ? { ...e, ...patch } : e));
    set(K.journal, next);
    return next;
  },

  // --- Capstone ---
  capstone: () => get(K.capstone, {
    quiz: { done: false, score: 0 },
    plan: { text: "", submittedAt: null },
    simDone: false,
    flashcardsReviewed: false,
    certificateIssued: false,
    issuedAt: null,
  }),
  saveCapstone: (partial) => {
    const cur = get(K.capstone, {
      quiz: { done: false, score: 0 },
      plan: { text: "", submittedAt: null },
      simDone: false,
      flashcardsReviewed: false,
      certificateIssued: false,
      issuedAt: null,
    });
    const next = { ...cur, ...partial };
    set(K.capstone, next);
    return next;
  },

  // --- Lesson completion (LessonsHub Tier 1 quizzes) ---
  lessonCompletion: () => get(K.lessonCompletion, {}),
  markLessonPassed: (lessonId, score) => {
    const cur = get(K.lessonCompletion, {});
    const prev = cur[lessonId] || {};
    cur[lessonId] = {
      passed: true,
      score,
      perfectEver: prev.perfectEver || score >= 0.999,
      ts: Date.now(),
    };
    set(K.lessonCompletion, cur);
    return cur;
  },
  moduleBonusAwarded: () => get(K.moduleBonus, {}),
  markModuleBonus: (moduleId) => {
    const cur = get(K.moduleBonus, {});
    cur[moduleId] = Date.now();
    set(K.moduleBonus, cur);
    return cur;
  },
  tier1Graduate: () => get(K.tier1Grad, null),
  markTier1Graduate: () => {
    set(K.tier1Grad, Date.now());
  },
  tier2Graduate: () => get(K.tier2Grad, null),
  markTier2Graduate: () => {
    set(K.tier2Grad, Date.now());
  },
  tier2GateConfirmed: () => get(K.tier2GateConfirmed, null),
  markTier2GateConfirmed: (payload) => {
    set(K.tier2GateConfirmed, { confirmedAt: Date.now(), ...(payload || {}) });
  },
  tier3Graduate: () => get(K.tier3Grad, null),
  markTier3Graduate: () => {
    set(K.tier3Grad, Date.now());
  },
  tier3GateConfirmed: () => get(K.tier3GateConfirmed, null),
  markTier3GateConfirmed: (payload) => {
    set(K.tier3GateConfirmed, { confirmedAt: Date.now(), ...(payload || {}) });
  },
  tier4Graduate: () => get(K.tier4Grad, null),
  markTier4Graduate: () => {
    set(K.tier4Grad, Date.now());
  },
  tier4GateConfirmed: () => get(K.tier4GateConfirmed, null),
  markTier4GateConfirmed: (payload) => {
    set(K.tier4GateConfirmed, { confirmedAt: Date.now(), ...(payload || {}) });
  },
  tier5Graduate: () => get(K.tier5Grad, null),
  markTier5Graduate: () => {
    set(K.tier5Grad, Date.now());
  },
  tier5GateConfirmed: () => get(K.tier5GateConfirmed, null),
  markTier5GateConfirmed: (payload) => {
    set(K.tier5GateConfirmed, { confirmedAt: Date.now(), ...(payload || {}) });
  },
  // Backend-signed tier certificates (Tier 2/3/4 — Tier 1 uses the capstone cert path)
  tierCerts: () => get(K.tierCerts, {}),
  storeTierCert: (tier, cert) => {
    const cur = get(K.tierCerts, {});
    cur[String(tier)] = cert;
    set(K.tierCerts, cur);
  },

  // For testing / reset
  resetAll: () => {
    Object.values(K).forEach((k) => localStorage.removeItem(k));
  },

  // --- Snapshot / Restore (for cross-device sync) ---
  snapshot: () => ({
    onboarded: get(K.onboarded, false),
    profile: get(K.profile, null),
    diagnosticDone: get(K.diagnosticDone, false),
    diagnosticScore: get(K.diagnosticScore, 0),
    track: get(K.track, null),
    graph: get(K.graph, null),
    xp: get(K.xp, 0),
    achievements: get(K.achievements, []),
    streak: get(K.streak, { days: 0, lastVisit: null }),
    gates: get(K.gates, {}),
    flashcards: get(K.flashcards, {}),
    journal: get(K.journal, []),
    capstone: get(K.capstone, null),
    lessonCompletion: get(K.lessonCompletion, {}),
    moduleBonus: get(K.moduleBonus, {}),
    tier1Grad: get(K.tier1Grad, null),
    tier2Grad: get(K.tier2Grad, null),
    tier2GateConfirmed: get(K.tier2GateConfirmed, null),
    tier3Grad: get(K.tier3Grad, null),
    tier3GateConfirmed: get(K.tier3GateConfirmed, null),
    tier4Grad: get(K.tier4Grad, null),
    tier4GateConfirmed: get(K.tier4GateConfirmed, null),
    tier5Grad: get(K.tier5Grad, null),
    tier5GateConfirmed: get(K.tier5GateConfirmed, null),
    tierCerts: get(K.tierCerts, {}),
    // Legacy monolithic course completion (Beginner/Advanced/CFD/Commodities/
    // Roadmap). These live under their own localStorage keys and MUST be synced
    // or users lose course progress on logout / new device / cache clear.
    courses: {
      flexx_v5: get("flexx_v5", null),
      flexx_adv_v5: get("flexx_adv_v5", null),
      flexx_cfd_v5: get("flexx_cfd_v5", null),
      flexx_com_v5: get("flexx_com_v5", null),
      flexx_rm_v5: get("flexx_rm_v5", null),
    },
  }),

  restore: (snap) => {
    if (!snap || typeof snap !== "object") return;
    if (snap.onboarded !== undefined) set(K.onboarded, !!snap.onboarded);
    if (snap.profile) set(K.profile, snap.profile);
    if (snap.diagnosticDone !== undefined) set(K.diagnosticDone, !!snap.diagnosticDone);
    if (snap.diagnosticScore !== undefined) set(K.diagnosticScore, snap.diagnosticScore);
    if (snap.track !== undefined) set(K.track, snap.track);
    if (snap.graph) set(K.graph, snap.graph);
    if (snap.xp !== undefined) set(K.xp, snap.xp);
    if (Array.isArray(snap.achievements)) set(K.achievements, snap.achievements);
    if (snap.streak) set(K.streak, snap.streak);
    if (snap.gates) set(K.gates, snap.gates);
    if (snap.flashcards) set(K.flashcards, snap.flashcards);
    if (Array.isArray(snap.journal)) set(K.journal, snap.journal);
    if (snap.capstone) set(K.capstone, snap.capstone);
    if (snap.lessonCompletion) set(K.lessonCompletion, snap.lessonCompletion);
    if (snap.moduleBonus) set(K.moduleBonus, snap.moduleBonus);
    if (snap.tier1Grad) set(K.tier1Grad, snap.tier1Grad);
    if (snap.tier2Grad) set(K.tier2Grad, snap.tier2Grad);
    if (snap.tier2GateConfirmed) set(K.tier2GateConfirmed, snap.tier2GateConfirmed);
    if (snap.tier3Grad) set(K.tier3Grad, snap.tier3Grad);
    if (snap.tier3GateConfirmed) set(K.tier3GateConfirmed, snap.tier3GateConfirmed);
    if (snap.tier4Grad) set(K.tier4Grad, snap.tier4Grad);
    if (snap.tier4GateConfirmed) set(K.tier4GateConfirmed, snap.tier4GateConfirmed);
    if (snap.tier5Grad) set(K.tier5Grad, snap.tier5Grad);
    if (snap.tier5GateConfirmed) set(K.tier5GateConfirmed, snap.tier5GateConfirmed);
    if (snap.tierCerts) set(K.tierCerts, snap.tierCerts);
    // Restore legacy course completion. UNION-merge the `completed` maps so a
    // stale server pull can never wipe progress that exists locally — course
    // completion must only ever grow, never regress.
    if (snap.courses && typeof snap.courses === "object") {
      ["flexx_v5", "flexx_adv_v5", "flexx_cfd_v5", "flexx_com_v5", "flexx_rm_v5"].forEach((sk) => {
        const incoming = snap.courses[sk];
        if (!incoming || typeof incoming !== "object") return;
        const existing = get(sk, null);
        let merged = incoming;
        if (existing && typeof existing === "object") {
          const completed = { ...(existing.completed || {}), ...(incoming.completed || {}) };
          merged = { ...existing, ...incoming, completed };
        }
        set(sk, merged);
      });
    }
  },
};

// Global event bus so XP toasts can listen without prop drilling
const listeners = {};
export const flexxEvents = {
  on: (evt, cb) => {
    listeners[evt] = listeners[evt] || [];
    listeners[evt].push(cb);
    return () => {
      listeners[evt] = (listeners[evt] || []).filter((f) => f !== cb);
    };
  },
  emit: (evt, data) => {
    (listeners[evt] || []).forEach((f) => { try { f(data); } catch (err) { console.error("[flexxEvents] listener error:", evt, err); } });
  },
};

// Helper: award XP and emit toast
export const awardXP = (amount, reason) => {
  const res = flexxState.addXp(amount);
  flexxEvents.emit("xp", { amount, reason, ...res });
  if (res.leveledUp) flexxEvents.emit("levelup", { level: res.newLevel });
  return res;
};
